import axios, { AxiosResponse } from 'axios'
import { criarRequestsDocumentos } from 'app/modules/apps/shared/documentos-pessoa/_requests'
import { ID, Response } from '../../../../../../_metronic/helpers'
import {
  AlergiaDto,
  DoencaDto,
  PacienteDto,
  PacienteDtoAdd,
  PacienteDtoCreate,
  PacienteDtoDadosAdicionais,
  PacienteDtoDadosSaude,
  PacienteDtoUpdate,
  PacienteDtoUpdateAso,
  PacienteList,
  PacientesQueryResponse,
  PacienteView,
  RemedioDto,
} from './_models'
import { Picture } from '../../../../auth'
import {
  EnderecoDtoCreate,
  EnderecoDtoUpdate,
  EnderecoList,
  EnderecoView,
} from '../../../empresa/empresas-list/core/_models'
import { ArquivoDto } from '../../../atendimento/core/_models'

const API_URL = process.env.REACT_APP_API_URL
const PACIENTE_URL = `${API_URL}/pacientes`
const PACIENTES_EMPRESAS_URL = `${API_URL}/pacientes/empresa/`
const GET_PACIENTES_URL = `${API_URL}/pacientes`
const ATENDIMENTO_PRONTUARIO_DOWNLOAD_URL = `${API_URL}/atendimentos/prontuario`

export const getPacientes = (query: string): Promise<PacientesQueryResponse> => {
  return axios
    .get(`${GET_PACIENTES_URL}?${decodeURIComponent(query)}`)
    .then((d: AxiosResponse<PacientesQueryResponse>) => d.data)
}
export const getPacientesEmpresa = (id: string, query: string): Promise<PacientesQueryResponse> => {
  return axios
    .get(`${PACIENTES_EMPRESAS_URL}${id}?${query}`)
    .then((d: AxiosResponse<PacientesQueryResponse>) => d.data)
}

export const getPacienteById = (id: ID): Promise<PacienteView> => {
  return axios
    .get(`${PACIENTE_URL}/${id}`)
    .then((response: AxiosResponse<PacienteView>) => response.data)
}

/**
 * Download do prontuário do paciente
 * @param idPaciente Identificador do paciente
 * @returns ArquivoDto
 */
export const downloadProntuario = (idPaciente: number): Promise<ArquivoDto> => {
  return axios
    .get(`${ATENDIMENTO_PRONTUARIO_DOWNLOAD_URL}/${idPaciente}`)
    .then((response: AxiosResponse<ArquivoDto>) => response.data)
}

/**
 * Busca todos prontuários do usuário autenticado.
 *
 * @returns {Promise<PacienteView[]>} Uma promisse que retorna com os prontuários do usuário autenticado.
 */
export const getProntuarioPacienteLogado = (): Promise<PacienteView[]> => {
  return axios
    .get(`${PACIENTE_URL}/me`)
    .then((response: AxiosResponse<PacienteView[]>) => response.data)
}

/**
 * Registra um paciente na empresa passada nos paramêtros
 * @param idEmpresa Identifcador da empresa
 * @param paciente Objeto PacienteDtoCreate
 * @returns PacienteView
 */
export const createPaciente = (
  idEmpresa: ID,
  paciente: PacienteDtoCreate
): Promise<PacienteView | undefined> => {
  return axios
    .post(`${PACIENTE_URL}/empresa/${idEmpresa}`, paciente)
    .then((response: AxiosResponse<PacienteView>) => response.data)
}

/**
 * Completa o cadastro de paciente (ASO)
 * @param id Identificador do paciente
 * @param pacienteAso Objeto PacienteDtoUpdateAso
 * @returns PacienteView
 */
export const updateAsoPaciente = (
  id: ID,
  pacienteAso: PacienteDtoUpdateAso
): Promise<PacienteView | undefined> => {
  return axios
    .put(PACIENTE_URL + '/aso/' + id, pacienteAso)
    .then((response: AxiosResponse<PacienteView>) => response.data)
}

/**
 * Cadastra um endereço
 * @param idPaciente Identificador do paciente
 * @param endereco Objeto EnderecoDtoCreate
 * @returns EnderecoView
 */
export const createEnderecoPaciente = (
  idPaciente: ID,
  endereco: EnderecoDtoCreate
): Promise<EnderecoView | undefined> => {
  return axios.post(`${PACIENTE_URL}/endereco/${idPaciente}`, endereco)
}
/**
 * Atualiza o endereço do paciente
 * @param pacienteId Identificador do paciente
 * @param endereco Objeto EnderecoDtoUpdate
 * @returns EnderecoView
 */
export const updateEnderecoPaciente = (
  pacienteId: number,
  endereco: EnderecoDtoUpdate
): Promise<EnderecoView | undefined> => {
  return axios.put(`${PACIENTE_URL}/endereco/${pacienteId}`, endereco)
}
export const getEnderecoByIdPaciente = (id: ID) => {
  return axios.get<EnderecoList[]>(`${PACIENTE_URL}/endereco/${id}`)
}

/**
 *
 * @param empresaId Identificador da empresa
 * @param paciente Objeto PacienteDtoAdd do paciente
 * @returns PacienteView
 */
export const vincularPacienteEmpresa = (empresaId: number, paciente: PacienteDtoAdd) => {
  return axios
    .put(`${PACIENTE_URL}/empresa/${empresaId}`, paciente)
    .then((resp: AxiosResponse<PacienteView>) => resp.data)
}

/**
 * Atualiza os dados do paciente
 * @param idPaciente Identificador do paciente
 * @param paciente Objeto PacienteDtoUpdate
 * @returns PacienteView
 */
export const updatePaciente = (
  idPaciente: number,
  paciente: PacienteDtoUpdate
): Promise<PacienteView | undefined> => {
  return axios
    .put(`${PACIENTE_URL}/${idPaciente}`, paciente)
    .then((response: AxiosResponse<PacienteView>) => response.data)
}

export const atualizaEmailPaciente = (
  idPaciente: ID,
  email: string
): Promise<PacienteView | undefined> => {
  return axios
    .put(`${PACIENTE_URL}/${idPaciente}`, {
      email: email,
    })
    .then((response: AxiosResponse<PacienteView>) => response.data)
}

export const adicionaAlergia = (idPaciente: ID, descricao: String): Promise<any> => {
  return axios
    .put(`${PACIENTE_URL}/alergia/${idPaciente}`, {
      descricao,
    })
    .then((response: AxiosResponse<Response<any>>) => response.data)
    .then((response: Response<any>) => response.data)
}

export const deletePaciente = (pacienteId: ID): Promise<void> => {
  return axios.delete(`${PACIENTE_URL}/${pacienteId}`)
}

export const deleteSelectedPaciente = (pacienteIds: Array<ID>): Promise<void> => {
  const requests = pacienteIds.map((id) => axios.delete(`${PACIENTE_URL}/${id}`))
  return axios.all(requests).then(() => {})
}

/**
 * Busca foto do paciente
 * @param idPaciente ID do paciente
 * @returns Avatar - string
 */
export const getAvatarPaciente = (idPaciente: ID): Promise<string> => {
  return axios
    .get(`${PACIENTE_URL}/${idPaciente}/avatar`)
    .then((response: AxiosResponse<Picture>) => response.data.data)
}

/**
 * Busca um paciente por empresa e pessoa
 * @param empresaId Identificador da empresa
 * @param pessoaId Identificador da pessoa
 * @returns PacienteList
 */
export const getPacienteByPessoaId = (
  empresaId: number,
  pessoaId: number
): Promise<PacienteList> => {
  return axios
    .get(`${PACIENTE_URL}/empresa/${empresaId}/pessoa/${pessoaId}`)
    .then((response: AxiosResponse<PacienteList>) => response.data)
}

/**
 * Atualiza dados adicionais de paciente
 * @param pacienteId Identificador do paciente
 * @param data PacienteDtoDadosAdicionais
 * @returns PacienteView
 */
export const updateDadosAdicionaisPaciente = (
  pacienteId: number,
  data: PacienteDtoDadosAdicionais
): Promise<PacienteView> => {
  return axios
    .put(`${PACIENTE_URL}/dados/${pacienteId}`, data)
    .then((response: AxiosResponse<PacienteView>) => response.data)
}

/**
 * Adiciona alergia a um paciente
 * @param pacienteId Identificador do paciente
 * @param data AlergiaDto
 * @returns PacienteDto
 */
export const updateAlergiaPaciente = (
  pacienteId: number,
  data: AlergiaDto
): Promise<PacienteDto> => {
  return axios
    .put(`${PACIENTE_URL}/alergia/${pacienteId}`, data)
    .then((response: AxiosResponse<PacienteDto>) => response.data)
}

/**
 * Remove alergia de um paciente
 * @param pacienteId Identificador do paciente
 * @param alergiaId Identificador da alergia
 * @returns void
 */
export const deleteAlergiaPaciente = (pacienteId: number, alergiaId: number): Promise<void> => {
  return axios
    .delete(`${PACIENTE_URL}/alergia/${pacienteId}/${alergiaId}`)
    .then((response: AxiosResponse<void>) => response.data)
}

/**
 * Atualiza doença crônica a um paciente
 * @param pacienteId Identificador do paciente
 * @param data DoencaDto
 * @returns PacienteDto
 */
export const updateDoencaPaciente = (pacienteId: number, data: DoencaDto): Promise<PacienteDto> => {
  return axios
    .put(`${PACIENTE_URL}/doenca/${pacienteId}`, data)
    .then((response: AxiosResponse<PacienteDto>) => response.data)
}

/**
 * Remove uma doença crônica de um paciente
 * @param pacienteId Identificador do paciente
 * @param doencaId Identificador da doença
 * @returns void
 */
export const deleteDoencaPaciente = (pacienteId: number, doencaId: number): Promise<void> => {
  return axios
    .delete(`${PACIENTE_URL}/doenca/${pacienteId}/${doencaId}`)
    .then((response: AxiosResponse<void>) => response.data)
}

/**
 * Adiciona medicamento em uso a um paciente
 * @param pacienteId Identificador do paciente
 * @param data RemedioDto
 * @returns PacienteDto
 */
export const updateRemedioPaciente = (
  pacienteId: number,
  data: RemedioDto
): Promise<PacienteDto> => {
  return axios
    .put(`${PACIENTE_URL}/medicamento/${pacienteId}`, data)
    .then((response: AxiosResponse<PacienteDto>) => response.data)
}

/**
 * Remove medicamento em uso de um paciente
 * @param pacienteId Identificador do paciente
 * @param remedioId Identificador do remedio
 * @returns void
 */
export const deleteRemedioPaciente = (pacienteId: number, remedioId: number): Promise<void> => {
  return axios
    .delete(`${PACIENTE_URL}/medicamento/${pacienteId}/${remedioId}`)
    .then((response: AxiosResponse<void>) => response.data)
}

/**
 * Atualiza dados de saúde do paciente
 * @param pacienteId Identificador do paciente
 * @param data PacienteDtoDadosSaude
 * @returns PacienteDto
 */
export const updateSaudePaciente = (
  pacienteId: number,
  data: PacienteDtoDadosSaude
): Promise<PacienteDto> => {
  return axios
    .put(`${PACIENTE_URL}/saude/${pacienteId}`, data)
    .then((response: AxiosResponse<PacienteDto>) => response.data)
}

/** Lista de documentos da pessoa (SBIS ECF.02.01): `GET/POST/PUT/DELETE /pacientes/documento`. */
export const documentosPacienteRequests = criarRequestsDocumentos(PACIENTE_URL)
